import { QueryBuilderProperty } from 'vet-bones/components';

export type VoiceboxWrapperRequest = {
  query: string;
  // TODO: replace `existingModel` with `existingOntology: string`
  existingModel?: VoiceboxModelSchema;
  conversationId: string;
  describe: boolean;
  retries?: number;
};

export type VoiceboxWrapperResponse = {
  content: string;
  conversationId: string;
  description?: string;
  errors?: string[];
};

// Internal types below-- may become obsolete when we move away from openai

export type VoiceboxBaseMessage = {
  id: string;
  role: 'assistant' | 'system' | 'user';
  content: string;
  hidden?: boolean;
};

export type VoiceboxInitialSystemMessage = VoiceboxBaseMessage & {
  role: 'system';
  isInitialSystem: true;
};

export type VoiceboxModelSchema = {
  classIris: string[];
  labelsByIri: Record<string, string>;
  propertiesByIri: Record<string, QueryBuilderProperty>;
};

export type VoiceboxExistingModelMessage = VoiceboxBaseMessage & {
  role: 'system';
  isExistingModel: true;
  humanText: string;
  modelSchema: VoiceboxModelSchema;
};

/** simple progress message, only for the user */
export type VoiceboxProgressMessage = VoiceboxBaseMessage & {
  role: 'assistant';
  isProgress: true;
};

/** ask for turtle for some user prompt */
export type VoiceboxTurtleRequestMessage = VoiceboxBaseMessage & {
  role: 'user';
  isTurtleRequest: true;
  userPrompt: string;
  /** if our response has errored */
  errored?: boolean;
};

/** get turtle for some user prompt */
export type VoiceboxTurtleResponseMessage = VoiceboxBaseMessage & {
  role: 'assistant';
  isTurtleResponse: true;
  /** id of the matching VoiceboxTurtleRequestMessage */
  requestMessageId: string;
  /** comment after the code block */
  after: string;
  /** comment before the code block */
  before: string;
  /** code inside the code block */
  code: string;
  /** the turtle response request failed, `content` now has the error message */
  errored?: boolean;

  /** id of a VoiceboxDescribeResponseMessage describing our code */
  describeResponseMessageId?: string;
};

/** ask for a description for some turtle */
export type VoiceboxDescribeRequestMessage = VoiceboxBaseMessage & {
  role: 'user';
  isDescribeRequest: true;
  code: string;
  /** if our response has errored */
  errored?: boolean;

  /** id of the matching VoiceboxTurtleResponseMessage */
  turtleResponseMessageId: string;
};

/** get turtle for some user prompt */
export type VoiceboxDescribeResponseMessage = VoiceboxBaseMessage & {
  role: 'assistant';
  isDescribeResponse: true;
  /** the describe response request failed, `content` now has the error message */
  errored?: boolean;

  /** id of the matching VoiceboxDescribeRequestMessage */
  requestMessageId: string;
};

export type VoiceboxMessage =
  | VoiceboxInitialSystemMessage
  | VoiceboxExistingModelMessage
  | VoiceboxProgressMessage
  | VoiceboxTurtleRequestMessage
  | VoiceboxTurtleResponseMessage
  | VoiceboxDescribeRequestMessage
  | VoiceboxDescribeResponseMessage;

export const isInitialSystemMessage = (
  message?: VoiceboxMessage
): message is VoiceboxInitialSystemMessage =>
  Boolean(message && 'isInitialSystem' in message && message.isInitialSystem);

export const isExistingModelMessage = (
  message?: VoiceboxMessage
): message is VoiceboxExistingModelMessage =>
  Boolean(message && 'isExistingModel' in message && message.isExistingModel);

export const isProgressMessage = (
  message?: VoiceboxMessage
): message is VoiceboxProgressMessage =>
  Boolean(message && 'isProgress' in message && message.isProgress);

export const isTurtleRequestMessage = (
  message?: VoiceboxMessage
): message is VoiceboxTurtleRequestMessage =>
  Boolean(message && 'isTurtleRequest' in message && message.isTurtleRequest);

export const isTurtleResponseMessage = (
  message?: VoiceboxMessage
): message is VoiceboxTurtleResponseMessage =>
  Boolean(message && 'isTurtleResponse' in message && message.isTurtleResponse);

export const isDescribeRequestMessage = (
  message?: VoiceboxMessage
): message is VoiceboxDescribeRequestMessage =>
  Boolean(
    message && 'isDescribeRequest' in message && message.isDescribeRequest
  );

export const isDescribeResponseMessage = (
  message?: VoiceboxMessage
): message is VoiceboxDescribeResponseMessage =>
  Boolean(
    message && 'isDescribeResponse' in message && message.isDescribeResponse
  );
